import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, StatusBadge, Btn, Label, Input, Textarea, Select } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarClock, AlertTriangle, Briefcase, Users, Inbox, TrendingUp, Megaphone, Send, Moon, RefreshCw } from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [bOpen, setBOpen] = useState(false);
  const [bAud, setBAud] = useState("all");
  const [bTitle, setBTitle] = useState("");
  const [bMsg, setBMsg] = useState("");
  const [bEmail, setBEmail] = useState(false);
  const [bSending, setBSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [quiet, setQuiet] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  const loadAnn = () => {
    api.get("/announcements").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/settings/quiet-hours").then((r) => setQuiet(r.data)).catch(() => {});
  };
  useEffect(() => { if (bOpen) loadAnn(); }, [bOpen]);

  const resend = async (id) => {
    try { const { data } = await api.post(`/announcements/${id}/resend`); toast.success(`Resent to ${data.recipients} people`); loadAnn(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveQuiet = async (patch) => {
    const next = { ...quiet, ...patch };
    setQuiet(next);
    try { await api.put("/settings/quiet-hours", next); toast.success("Quiet hours updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendBroadcast = async () => {
    if (!bTitle.trim()) { toast.error("Add a title"); return; }
    setBSending(true);
    try {
      const { data: res } = await api.post("/broadcast", { audience: bAud, title: bTitle, message: bMsg, send_email: bEmail });
      toast.success(`Announcement sent to ${res.recipients} people${bEmail ? ` · ${res.emailed} emailed` : ""}`);
      setBTitle(""); setBMsg(""); loadAnn();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBSending(false); }
  };

  if (!data) return <div className="font-mono text-zinc-500 animate-pulse">{t("dashboard.loading")}</div>;

  const stats = [
    { key: "total-jobs", label: t("dashboard.totalJobs"), value: data.total_jobs, icon: Briefcase, color: "#FF5722" },
    { key: "new-requests", label: t("dashboard.newRequests"), value: data.new_requests, icon: Inbox, color: "#3B82F6" },
    { key: "crew-members", label: t("dashboard.crewMembers"), value: data.crew_count, icon: Users, color: "#22C55E" },
    { key: "overdue", label: t("dashboard.overdue"), value: data.overdue.length, icon: AlertTriangle, color: "#EF4444" },
  ];

  const pipeline = [
    { k: "draft", label: "Draft" },
    { k: "sent", label: "Sent" },
    { k: "approved", label: "Approved" },
    { k: "declined", label: "Declined" },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4 animate-rise">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-1">{t("dashboard.eyebrow")}</div>
          <h1 className="font-head font-black uppercase text-3xl sm:text-4xl text-white tracking-tight">{t("dashboard.title")}</h1>
        </div>
        <Btn data-testid="announce-btn" onClick={() => setBOpen(true)}><Megaphone size={16} /> {t("dashboard.announce")}</Btn>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className="p-5 animate-rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`stat-${s.key}`}>
              <Icon size={22} style={{ color: s.color }} />
              <div className="font-head font-black text-4xl text-white mt-3">{s.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.label}</div>
            </Card>
          );
        })}
      </div>

      {/* Today & Overdue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 [&>*]:min-w-0">
        <Card className="p-5" data-testid="today-section">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock size={20} className="text-[#FF5722]" />
            <h2 className="font-head font-extrabold uppercase text-lg text-white">{t("dashboard.today")}</h2>
            <span className="font-mono text-xs text-zinc-500">({data.today.length})</span>
          </div>
          <JobMiniList jobs={data.today} navigate={navigate} empty={t("dashboard.nothingToday")} />
        </Card>

        <Card className="p-5 border-red-900/60" data-testid="overdue-section">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={20} className="text-red-500" />
            <h2 className="font-head font-extrabold uppercase text-lg text-white">{t("dashboard.overdue")}</h2>
            <span className="font-mono text-xs text-zinc-500">({data.overdue.length})</span>
          </div>
          <JobMiniList jobs={data.overdue} navigate={navigate} empty={t("dashboard.allCaught")} />
        </Card>
      </div>

      {/* Bid pipeline */}
      <Card className="p-5" data-testid="bid-pipeline">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={20} className="text-[#FF5722]" />
          <h2 className="font-head font-extrabold uppercase text-lg text-white">{t("dashboard.bidPipeline")}</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {pipeline.map((p) => (
            <div key={p.k} className="bg-zinc-950 border-2 border-zinc-800 p-4">
              <StatusBadge status={p.k} />
              <div className="font-head font-black text-3xl text-white mt-3">{data.pipeline[p.k] || 0}</div>
              <div className="font-mono text-xs text-zinc-500 mt-1">{money(data.pipeline_value[p.k])}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Announcement broadcast */}
      <Dialog open={bOpen} onOpenChange={setBOpen}>
        <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head font-black uppercase flex items-center gap-2"><Megaphone size={18} className="text-[#FF5722]" /> Send Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">Push a pop-up notification to everyone (or a group). They'll see it in-app instantly, and optionally by email.</p>
            <div><Label>Audience</Label><Select data-testid="announce-audience" value={bAud} onChange={(e) => setBAud(e.target.value)}><option value="all">Everyone</option><option value="clients">Clients only</option><option value="crew">Contractors only</option></Select></div>
            <div><Label>Title</Label><Input data-testid="announce-title" value={bTitle} onChange={(e) => setBTitle(e.target.value)} placeholder="Holiday schedule update" /></div>
            <div><Label>Message</Label><Textarea data-testid="announce-message" value={bMsg} onChange={(e) => setBMsg(e.target.value)} placeholder="Write your announcement…" className="min-h-[100px]" /></div>
            <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" data-testid="announce-email" checked={bEmail} onChange={(e) => setBEmail(e.target.checked)} className="w-4 h-4 accent-[#FF5722]" /> Also send as email</label>
            <Btn data-testid="announce-send-btn" onClick={sendBroadcast} disabled={bSending} className="w-full"><Send size={16} /> {bSending ? "Sending…" : "Send"}</Btn>

            {quiet && (
              <div className="border-t border-zinc-800 pt-4 space-y-2" data-testid="quiet-hours">
                <label className="flex items-center justify-between gap-2 text-sm text-zinc-300">
                  <span className="flex items-center gap-1.5"><Moon size={14} className="text-[#FF5722]" /> Quiet hours (hold non-urgent pushes overnight)</span>
                  <input type="checkbox" data-testid="quiet-enabled" checked={!!quiet.enabled} onChange={(e) => saveQuiet({ enabled: e.target.checked })} className="w-4 h-4 accent-[#FF5722]" />
                </label>
                {quiet.enabled && (
                  <div className="flex items-center gap-2 text-xs text-zinc-400">
                    <span>From</span>
                    <Select data-testid="quiet-start" value={quiet.start} onChange={(e) => saveQuiet({ start: parseInt(e.target.value) })} className="w-24">{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</Select>
                    <span>to</span>
                    <Select data-testid="quiet-end" value={quiet.end} onChange={(e) => saveQuiet({ end: parseInt(e.target.value) })} className="w-24">{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</Select>
                  </div>
                )}
                <p className="text-[11px] text-zinc-600">Urgent alerts (job assignments) always go out immediately. Messages & announcements held during quiet hours are delivered at the end time.</p>
              </div>
            )}

            <div className="border-t border-zinc-800 pt-4" data-testid="announce-history">
              <div className="font-head font-bold uppercase text-xs tracking-wide text-[#FF5722] mb-2">Recent announcements</div>
              {history.length === 0 ? <div className="text-zinc-600 font-mono text-xs">None sent yet.</div> : (
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {history.map((a) => (
                    <div key={a.id} data-testid={`ann-${a.id}`} className="border border-zinc-800 bg-black/30 p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-white text-sm font-semibold truncate">{a.title}</span>
                        <button data-testid={`resend-${a.id}`} onClick={() => resend(a.id)} className="shrink-0 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-[#FF5722] hover:underline"><RefreshCw size={11} /> Resend</button>
                      </div>
                      {a.message && <div className="text-zinc-500 text-xs mt-0.5 line-clamp-2">{a.message}</div>}
                      <div className="font-mono text-[10px] text-zinc-600 mt-1">{a.scope === "crew" ? "Contractors" : (a.audience === "all" ? "Everyone" : a.audience)} · {a.recipients} sent{a.emailed ? ` · ${a.emailed} emailed` : ""} · {new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function JobMiniList({ jobs, navigate, empty }) {
  if (!jobs.length) return <div className="text-zinc-600 text-sm font-mono py-4">{empty}</div>;
  return (
    <div className="space-y-2">
      {jobs.map((j) => (
        <button
          key={j.id}
          data-testid={`dash-job-${j.id}`}
          onClick={() => navigate(`/jobs/${j.id}`)}
          className="w-full text-left flex items-center justify-between gap-3 bg-zinc-950 border-2 border-zinc-800 hover:border-[#FF5722] p-3 transition-colors"
        >
          <div className="min-w-0">
            <div className="text-white font-semibold text-sm truncate">{j.address}</div>
            <div className="font-mono text-[11px] text-zinc-500">{j.service_type} · {j.assigned_crew_name || "Unassigned"}</div>
          </div>
          <StatusBadge status={j.status} />
        </button>
      ))}
    </div>
  );
}
